/**
 * Shopping Lists Page
 * Main page showing all shopping lists with creation, templates, and management
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  ShoppingCart, 
  ListChecks,
  Archive,
  ArchiveRestore,
  Filter,
  Search,
  MoreVertical,
  Copy,
  Share2,
  Trash2,
  CheckCircle2,
  TrendingUp,
  Calendar,
  Users,
  Star,
  Loader2,
  Package,
  Eye,
  PlusCircle,
  RefreshCw,
  Sparkles,
  ArrowUpDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from "@/contexts/AuthContext";
import {
  getShoppingLists, 
  deleteShoppingList,
  duplicateShoppingList,
  archiveList,
  completeList,
  getShoppingListStats,
  updateShoppingList,
  LIST_STATUS
} from '@/services/shoppingListService';
import { completeJourneyStep, JOURNEY_STEPS } from '@/services/journeyService';
import DashboardHeader from '@/components/DashboardHeader';
import BottomNavigation from '@/components/BottomNavigation';
import CreateListModal from '@/components/CreateListModal';
// ListDetailView removed - now navigating to standalone ShoppingListPage
import Modal from '@/components/ui/Modal';
import ShareModal from '@/components/modals/ShareModal';
import Tooltip from '@/components/common/Tooltip';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
// Smart Purchase Planner 2.0 - Phase 4: Gamification
import GamificationPanel from '@/components/shopping/GamificationPanel';

const PAGE_SIZE = 20;

export default function ShoppingListsPage() {
  const { user, currency: userCurrency } = useAuth();
  const currency = userCurrency || 'GBP';
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  
  const [lists, setLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, mostItems, alphabetical
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalStartStep, setCreateModalStartStep] = useState(1); // 1: template selection, 2: blank list customization
  // selectedList state removed - now navigating to /shopping-list/:listId instead
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteMutations, setFavoriteMutations] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const loadMoreTriggerRef = useRef(null);
  const [listStats, setListStats] = useState({
    active: 0,
    completed: 0,
    archived: 0,
    totalItems: 0,
    totalLists: 0
  });

  const fetchListStats = useCallback(async () => {
    if (!user) return;

    try {
      const statsResponse = await getShoppingListStats(user.uid);
      if (!statsResponse) return;

      const {
        activeLists: active = 0,
        completedLists: completed = 0,
        totalItems = 0,
        totalLists = 0
      } = statsResponse;

      setListStats({
        active,
        completed,
        archived: 0,
        totalItems,
        totalLists
      });
    } catch (error) {
      console.error('Error loading list stats:', error);
    }
  }, [user]);

  const loadLists = useCallback(async ({ cursor: cursorOverride = null, append = false } = {}) => {
    if (!user) return;

    try {
      if (!append) {
        setLoading(true);
        setNextCursor(null);
      }

      const statusFilter = filterStatus === 'all' ? null : filterStatus;
      const { lists: fetchedLists = [], nextCursor: cursorToken } = await getShoppingLists(user.uid, {
        status: statusFilter,
        limit: PAGE_SIZE,
        cursor: cursorOverride,
        favoritesOnly: showFavoritesOnly
      });

      setLists((prev) => {
        if (!append) {
          return fetchedLists;
        }

        const existingIds = new Set(prev.map((list) => list.id));
        const merged = [...prev];
        for (const list of fetchedLists) {
          if (!existingIds.has(list.id)) {
            merged.push(list);
          }
        }
        return merged;
      });

      setNextCursor(cursorToken || null);
      if (!append) {
        await fetchListStats();
      }

      if (!append && fetchedLists.length > 0) {
        await completeJourneyStep(JOURNEY_STEPS.CREATED_SHOPPING_LIST);
        const hasItems = fetchedLists.some((list) => (list.itemCount || 0) > 0);
        if (hasItems) {
          await completeJourneyStep(JOURNEY_STEPS.ADDED_ITEMS);
        }
      }
    } catch (error) {
      console.error('Error loading lists:', error);
    } finally {
      if (!append) {
        setLoading(false);
      }
    }
  }, [user, filterStatus, showFavoritesOnly, completeJourneyStep, fetchListStats]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || isFetchingMore) {
      return;
    }

    setIsFetchingMore(true);
    try {
      await loadLists({ cursor: nextCursor, append: true });
    } finally {
      setIsFetchingMore(false);
    }
  }, [nextCursor, isFetchingMore, loadLists]);

  useEffect(() => {
    if (user) {
      loadLists();
    }
  }, [user, loadLists]);

  useEffect(() => {
    filterAndSortLists();
  }, [lists, searchQuery, sortBy]);

  useEffect(() => {
    if (!nextCursor) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const target = loadMoreTriggerRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        loadMore();
      }
    }, { threshold: 0.75 });

    observer.observe(target);

    return () => observer.disconnect();
  }, [nextCursor, loadMore]);

  const getListCreatedDate = (list) => {
    const candidates = [list?.createdAt, list?.updatedAt, list?.lastActivityAt];
    for (const value of candidates) {
      if (!value) continue;
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
    return new Date(0);
  };

  const getListEstimatedSpend = (list) => {
    const estimated = Number(list?.estimatedTotal);
    if (Number.isFinite(estimated) && estimated > 0) {
      return estimated;
    }
    const spent = Number(list?.totalSpent);
    if (Number.isFinite(spent) && spent > 0) {
      return spent;
    }
    return 0;
  };

  function filterAndSortLists() {
    let filtered = lists;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = lists.filter(list => 
        list.name.toLowerCase().includes(query) ||
        list.description?.toLowerCase().includes(query) ||
        list.templateId?.toLowerCase().includes(query) ||
        list.notes?.toLowerCase().includes(query) ||
        list.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return getListCreatedDate(b) - getListCreatedDate(a);
        case 'oldest':
          return getListCreatedDate(a) - getListCreatedDate(b);
        case 'mostItems':
          return (b.itemCount || 0) - (a.itemCount || 0);
        case 'alphabetical':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });
    
    setFilteredLists(sorted);
  }

  function filterLists() {
    if (!searchQuery.trim()) {
      setFilteredLists(lists);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = lists.filter(list => 
      list.name.toLowerCase().includes(query) ||
      list.description?.toLowerCase().includes(query) ||
      list.tags?.some(tag => tag.toLowerCase().includes(query))
    );
    
    setFilteredLists(filtered);
  }

  async function handleDeleteList(listId) {
    if (!user) return;
    await deleteShoppingList(user.uid, listId);
    loadLists();
  }

  const promptDeleteList = (list) => {
    setDeleteTarget(list);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const confirmDeleteList = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await handleDeleteList(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted`);
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting list:', error);
      
      // Check for business rule violation
      if (error?.code === 'LIST_HAS_BOUGHT_ITEMS' || error?.message?.includes('bought items')) {
        toast.error('This list contains bought items and cannot be deleted.', {
          duration: 5000,
          icon: '🛡️'
        });
      } else {
        toast.error(error?.message || 'Failed to delete list');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const promptShareList = (list) => {
    setShareTarget(list);
    setActiveDropdown(null);
  };

  const closeShareModal = () => {
    setShareTarget(null);
  };

  async function handleDuplicateList(listId) {
    if (!user) return;
    try {
      await duplicateShoppingList(user.uid, listId);
      loadLists();
    } catch (error) {
      console.error('Error duplicating list:', error);
    }
  }

  async function handleArchiveList(listId) {
    if (!user) return;
    try {
      await archiveList(user.uid, listId, true);
      loadLists();
    } catch (error) {
      console.error('Error archiving list:', error);
    }
  }

  async function handleCompleteList(listId) {
    if (!user) return;
    try {
      const list = lists.find(l => l.id === listId);
      if (!list) return;
      
      const unboughtCount = (list.itemCount || 0) - (list.purchasedCount || 0);
      
      // Confirm with user if there are unbought items
      if (unboughtCount > 0) {
        const confirmed = await confirm({
          title: 'Complete List',
          message: `This list has ${unboughtCount} unbought item${unboughtCount === 1 ? '' : 's'}.\n\nMarking as complete will keep all ${list.purchasedCount || 0} purchased items for financial analysis and remove all ${unboughtCount} unbought items.\n\nContinue?`,
          variant: 'warning',
          confirmText: 'Complete'
        });
        
        if (!confirmed) return;
      }
      
      const result = await completeList(user.uid, listId);
      
      if (result.removedItemsCount > 0) {
        toast.success(`List completed! Removed ${result.removedItemsCount} unbought item${result.removedItemsCount === 1 ? '' : 's'}.`);
      } else {
        toast.success('List marked as completed!');
      }
      
      loadLists();
    } catch (error) {
      console.error('Error completing list:', error);
      toast.error('Failed to complete list');
    }
  }

  async function handleToggleFavorite(event, targetList) {
    event.stopPropagation();
    if (!user || !targetList) return;

    const nextValue = !targetList.favorite;
    const previousLists = lists;

    setFavoriteMutations((prev) => ({
      ...prev,
      [targetList.id]: true
    }));

    setLists((prev) => prev.map((list) => (
      list.id === targetList.id
        ? { ...list, favorite: nextValue }
        : list
    )));

    try {
      await updateShoppingList(user.uid, targetList.id, { favorite: nextValue });

      if (showFavoritesOnly && !nextValue) {
        setLists((prev) => prev.filter((list) => list.id !== targetList.id));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setLists(previousLists);
    } finally {
      setFavoriteMutations((prev) => {
        const clone = { ...prev };
        delete clone[targetList.id];
        return clone;
      });
    }
  }

  function getProgressPercentage(list) {
    const totalItems = list.itemCount || 0;
    if (totalItems === 0) return 0;
    const completed = list.purchasedCount || 0;
    return Math.round((completed / totalItems) * 100);
  }

  function getBudgetStatus(list) {
    return null;
  }

  const derivedStats = useMemo(() => {
    const activeLists = lists.filter(
      l => l.status === LIST_STATUS.ACTIVE && l.archived !== true
    );
    const completedLists = lists.filter(l => l.status === LIST_STATUS.COMPLETED);
    const archivedLists = lists.filter(l => l.status === LIST_STATUS.ARCHIVED);
    
    // Calculate total pending items across active lists
    const itemsToBuy = activeLists.reduce((sum, l) => {
      const pending = (l.itemCount || 0) - (l.purchasedCount || 0);
      return sum + pending;
    }, 0);

    // Standalone mode: no budget/envelope or spend aggregation
    const estimatedSpendActive = 0;
    const hasEnvelopePlanned = false;
    const hasActiveEstimates = false;
    const totalSpentActive = 0;
    const hasActivePurchases = activeLists.some((l) => {
      const purchasedCount = Number(l.purchasedCount);
      return Number.isFinite(purchasedCount) && purchasedCount > 0;
    });

    return {
      active: activeLists.length,
      completed: completedLists.length,
      archived: archivedLists.length,
      itemsToBuy,
      estimatedSpendActive,
      hasActiveEstimates,
      hasEnvelopePlanned, // NEW: Flag for envelope-based planned
      totalSpentActive,
      hasActivePurchases,
      totalItems: lists.reduce((sum, l) => sum + (l.itemCount || 0), 0),
      totalLists: lists.length
    };
  }, [lists]);

  const stats = useMemo(() => ({
    active: typeof listStats.active === 'number' ? listStats.active : derivedStats.active,
    completed: typeof listStats.completed === 'number' ? listStats.completed : derivedStats.completed,
    archived: typeof listStats.archived === 'number' ? listStats.archived : derivedStats.archived,
    itemsToBuy: derivedStats.itemsToBuy,
    totalItems: typeof listStats.totalItems === 'number' ? listStats.totalItems : derivedStats.totalItems,
    totalLists: typeof listStats.totalLists === 'number' ? listStats.totalLists : derivedStats.totalLists
  }), [listStats, derivedStats]);

  // Calculate archived summary
  const archivedSummary = useMemo(() => {
    const archivedLists = lists.filter(l => l.status === LIST_STATUS.ARCHIVED);
    return {
      count: archivedLists.length
    };
  }, [lists]);

  async function handleUnarchiveList(listId) {
    if (!user) return;
    try {
      // Unarchive the list by setting shouldArchive to false
      await archiveList(user.uid, listId, false);
      toast.success('List unarchived');
      loadLists();
    } catch (error) {
      console.error('Error unarchiving list:', error);
      toast.error('Failed to unarchive list');
    }
  }

  async function handleRecreateList(listId) {
    if (!user) return;
    try {
      // Duplicate the list
      await duplicateShoppingList(user.uid, listId);
      toast.success('List recreated as new active list');
      loadLists();
    } catch (error) {
      console.error('Error recreating list:', error);
      toast.error('Failed to recreate list');
    }
  }

  // Note: List detail view now handled by navigating to /shopping-list/:listId
  // The selectedList state is no longer used for inline rendering

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9FBFB] to-white dark:from-slate-900 dark:to-slate-800">
      <main className="px-3 sm:px-4 md:px-6 py-4 sm:py-6 pb-20 sm:pb-24 max-w-7xl mx-auto">
        {/* Page Header - Elegant Design */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-2">
                Shopping Lists
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Organize, track, and share your shopping lists
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards - Elegant Design */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
          {/* Items To Buy */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl p-5 sm:p-6 border-2 border-gray-200 dark:border-slate-600 shadow-sm dark:shadow-slate-900/50 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#118AB2]/20 to-[#118AB2]/5 dark:from-[#118AB2]/30 dark:to-[#118AB2]/10 flex items-center justify-center">
                <Package size={24} className="text-[#118AB2]" />
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{stats.itemsToBuy}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Items To Buy</p>
              </div>
            </div>
          </motion.div>

          {/* Active Lists */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            onClick={() => setFilterStatus('active')}
            className={`bg-white dark:bg-slate-800 rounded-2xl p-5 sm:p-6 border-2 transition-all duration-300 cursor-pointer text-left shadow-sm dark:shadow-slate-900/50 hover:shadow-md ${
              filterStatus === 'active' 
                ? 'border-[#06D6A0] ring-2 ring-[#06D6A0]/20' 
                : 'border-gray-200 dark:border-slate-600 hover:border-[#06D6A0]'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#06D6A0]/20 to-[#06D6A0]/5 dark:from-[#06D6A0]/30 dark:to-[#06D6A0]/10 flex items-center justify-center">
                <ShoppingCart size={24} className="text-[#06D6A0]" />
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Lists</p>
              </div>
            </div>
          </motion.button>

          {/* Completed Lists */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => setFilterStatus('completed')}
            className={`bg-white dark:bg-slate-800 rounded-2xl p-5 sm:p-6 border-2 transition-all duration-300 cursor-pointer text-left shadow-sm dark:shadow-slate-900/50 hover:shadow-md ${
              filterStatus === 'completed' 
                ? 'border-blue-500 ring-2 ring-blue-500/20' 
                : 'border-gray-200 dark:border-slate-600 hover:border-blue-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 dark:from-blue-500/30 dark:to-blue-500/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-blue-500" />
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{stats.completed}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Completed Lists</p>
              </div>
            </div>
          </motion.button>

          {/* Archived Lists */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={() => setFilterStatus('archived')}
            className={`bg-white dark:bg-slate-800 rounded-2xl p-5 sm:p-6 border-2 transition-all duration-300 cursor-pointer text-left shadow-sm dark:shadow-slate-900/50 hover:shadow-md ${
              filterStatus === 'archived' 
                ? 'border-gray-500 ring-2 ring-gray-500/20' 
                : 'border-gray-200 dark:border-slate-600 hover:border-gray-400'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-400/20 to-gray-400/5 dark:from-gray-400/30 dark:to-gray-400/10 flex items-center justify-center">
                <Archive size={24} className="text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{stats.archived}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Archived Lists</p>
              </div>
            </div>
          </motion.button>
        </div>

        {/* Smart Purchase Planner 2.0 - Phase 4: Gamification Panel */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6 sm:mb-8"
        >
          <GamificationPanel
            totalXP={stats.totalItems * 5 + stats.completed * 50}
            shoppingHistory={lists
              .filter(l => l.status === LIST_STATUS.COMPLETED || l.status === LIST_STATUS.ARCHIVED)
              .map(l => ({
                date: l.completedAt || l.updatedAt,
                wasUnderBudget: (l.totalSpent || 0) <= (l.estimatedTotal || l.totalSpent || 0)
              }))}
            earnedBadgeIds={[
              ...(stats.completed >= 1 ? ['first_purchase'] : []),
              ...(stats.completed >= 10 ? ['shopping_pro'] : []),
              ...(stats.totalItems >= 500 ? ['item_master'] : []),
            ]}
            compact={true}
          />
        </motion.div>

        {/* Search & Filters */}
        <div className="flex flex-col gap-3 mb-6 sm:mb-8">
          {/* Search and Sort */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 sm:py-3.5 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-2xl 
                           focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                           text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all"
              />
            </div>

            {/* Sort By */}
            <div className="relative">
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-2xl px-4 py-3">
                <ArrowUpDown size={18} className="text-gray-600 dark:text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-sm font-semibold text-gray-700 dark:text-gray-200 focus:outline-none cursor-pointer"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="mostItems">Most items</option>
                  <option value="alphabetical">A-Z</option>
                </select>
              </div>
            </div>
          </div>

          {/* Filter Pills with elegant styling */}
          <div className="flex flex-wrap items-center gap-2">
            {['all', 'active', 'completed', 'archived'].map((status) => {
              const count = status === 'all' ? stats.active + stats.completed + stats.archived 
                          : status === 'active' ? stats.active 
                          : status === 'completed' ? stats.completed 
                          : stats.archived;
              return (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`
                    flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 border-2
                    ${filterStatus === status
                      ? status === 'active' 
                        ? 'bg-gradient-to-r from-[#06D6A0] to-emerald-500 text-white border-[#06D6A0] shadow-lg shadow-emerald-500/30 scale-105'
                        : status === 'completed'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-blue-500 shadow-lg shadow-blue-500/30 scale-105'
                        : status === 'archived'
                        ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white border-gray-500 shadow-lg shadow-gray-500/30 scale-105'
                        : 'bg-gradient-to-r from-[#006D77] to-teal-500 text-white border-[#006D77] shadow-lg shadow-teal-500/30 scale-105'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border-transparent hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 hover:scale-105 hover:shadow-md'
                    }
                  `}
                >
                  {status === 'active' && <ShoppingCart size={14} />}
                  {status === 'completed' && <CheckCircle2 size={14} />}
                  {status === 'archived' && <Archive size={14} />}
                  {status === 'all' && <ListChecks size={14} />}
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  {count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      filterStatus === status
                        ? 'bg-white/30 text-white'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            
            {/* Favorites Toggle */}
            <button
              type="button"
              onClick={() => setShowFavoritesOnly((prev) => !prev)}
              aria-pressed={showFavoritesOnly}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 border-2
                ${showFavoritesOnly
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white border-amber-400 shadow-lg shadow-amber-500/30 scale-105'
                  : 'text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent hover:border-amber-300 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:scale-105 hover:shadow-md'}
              `}
            >
              <Star size={14} className={showFavoritesOnly ? 'fill-current' : ''} />
              Favorites
            </button>
          </div>
        </div>

        {/* Create New List Buttons - Compact & Elegant */}
        <div className="flex gap-2 mb-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setCreateModalStartStep(2); // Skip to customization step for blank list
              setShowCreateModal(true);
            }}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#006D77] hover:bg-[#005a63] text-white 
                       rounded-full text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Plus size={16} />
            <span>New List</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setCreateModalStartStep(1); // Show template selection
              setShowCreateModal(true);
            }}
            className="flex items-center gap-1.5 px-4 py-2 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-[#006D77] dark:text-[#06D6A0]
                       rounded-full text-sm font-medium transition-all duration-200 
                       border border-[#006D77]/30 dark:border-slate-500 shadow-sm hover:shadow-md"
          >
            <Sparkles size={16} />
            <span>Template</span>
          </motion.button>
        </div>

        {/* Archived Summary (shown only on archived tab) */}
        {filterStatus === 'archived' && archivedSummary.count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-2xl p-4 mb-6"
          >
            <div className="flex items-center gap-3">
              <Archive size={20} className="text-gray-600 dark:text-gray-400" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You've archived <span className="font-bold">{archivedSummary.count}</span> list{archivedSummary.count !== 1 ? 's' : ''}.
              </p>
            </div>
          </motion.div>
        )}

        {/* Lists Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-2xl p-5 animate-pulse">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-slate-700" />
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-slate-700" />
                </div>
                <div className="mb-4">
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3 mb-2" />
                  <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full" />
                </div>
                <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl" />
              </div>
            ))}
          </div>
        ) : filteredLists.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            {searchQuery ? (
              <>
                <Search size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No lists found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Try a different search term
                </p>
              </>
            ) : filterStatus === 'active' ? (
              <>
                <ShoppingCart size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No active lists yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Start with a Weekly Groceries template or create a blank list
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setCreateModalStartStep(1); // Show template selection
                    setShowCreateModal(true);
                  }}
                  className="px-6 py-3 bg-[#006D77] text-white rounded-2xl font-semibold
                             shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2"
                >
                  <Sparkles size={18} />
                  Start with Weekly Groceries
                </motion.button>
              </>
            ) : filterStatus === 'completed' ? (
              <>
                <CheckCircle2 size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No completed lists
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Lists you mark as complete will appear here
                </p>
              </>
            ) : filterStatus === 'archived' ? (
              <>
                <Archive size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No archived lists
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Lists you archive will appear here
                </p>
              </>
            ) : (
              <>
                <ShoppingCart size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No shopping lists yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Create your first list to get started
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-[#006D77] text-white rounded-2xl font-semibold
                             shadow-lg hover:shadow-xl transition-all"
                >
                  Create Your First List
                </motion.button>
              </>
            )}
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {filteredLists.map((list, index) => {
              const progress = getProgressPercentage(list);
              const isArchived = list.status === LIST_STATUS.ARCHIVED;
              const isCompleted = list.status === LIST_STATUS.COMPLETED;
              const hasBoughtItems = (Number(list.purchasedCount) || 0) > 0;

              const getBudgetTintClasses = () => {
                return 'bg-white dark:bg-slate-800';
              };
              
              const getBudgetBorderClasses = () => {
                return 'border-gray-100 dark:border-slate-600 hover:border-[#006D77]/50 dark:hover:border-[#06D6A0]/50';
              };
              
              return (
                <motion.div
                  key={list.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, type: "spring", stiffness: 100 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className={`${getBudgetTintClasses()} rounded-2xl shadow-sm hover:shadow-xl 
                             border-2 ${getBudgetBorderClasses()}
                             transition-all duration-300 group overflow-hidden`}
                >
                  {/* Gradient Top Accent - reflects budget status */}
                  <div 
                    className="h-1.5 w-full"
                    style={{ 
                      background: `linear-gradient(90deg, ${list.color}, ${list.color}80)`
                    }}
                  />
                  
                  {/* Header - clickable area */}
                  <div 
                    onClick={() => navigate(`/shopping-list/${list.id}`)}
                    className="p-5 cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div 
                          className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 shadow-sm"
                          style={{ 
                            background: `linear-gradient(135deg, ${list.color}30, ${list.color}10)`,
                            border: `2px solid ${list.color}40`
                          }}
                        >
                          {list.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white text-lg truncate group-hover:text-[#006D77] dark:group-hover:text-[#06D6A0] transition-colors">
                            {list.name}
                          </h3>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Status Pill with gradient */}
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${
                          list.status === LIST_STATUS.ACTIVE 
                            ? 'bg-gradient-to-r from-[#06D6A0]/20 to-emerald-100 dark:from-[#06D6A0]/30 dark:to-emerald-900/50 text-[#059669] dark:text-emerald-300 border border-[#06D6A0]/30 dark:border-emerald-700' 
                            : list.status === LIST_STATUS.COMPLETED 
                            ? 'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700' 
                            : 'bg-gradient-to-r from-gray-100 to-gray-50 dark:from-slate-700 dark:to-slate-600 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600'
                        }`}>
                          {list.status}
                        </span>

                        {/* Favorite Star */}
                        <button
                          type="button"
                          aria-label={list.favorite ? 'Unstar list' : 'Star list'}
                          aria-pressed={list.favorite}
                          disabled={Boolean(favoriteMutations[list.id])}
                          onClick={(event) => handleToggleFavorite(event, list)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            list.favorite
                              ? 'text-amber-500'
                              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                          }`}
                        >
                          <Star size={16} className={list.favorite ? 'fill-current' : ''} />
                        </button>
                      </div>
                    </div>

                    {/* Middle - Progress and Budget */}
                    <div className="space-y-3 mb-4">
                      {/* Progress */}
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="text-gray-600 dark:text-gray-400">
                            <span className="font-bold text-gray-900 dark:text-white">{list.purchasedCount || 0}</span> / {list.itemCount || 0} items bought
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            className="h-full rounded-full shadow-sm"
                            style={{ 
                              background: `linear-gradient(90deg, ${list.color}, ${list.color}CC)`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Item Summary */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          Items:{' '}
                          <span className="font-bold text-gray-900 dark:text-white">
                            {list.itemCount || 0}
                          </span>
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          Purchased:{' '}
                          <span className="font-bold text-gray-900 dark:text-white">
                            {list.purchasedCount || 0}
                          </span>
                        </span>
                      </div>

                      {/* Due Date */}
                      {list.dueDate && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <Calendar size={14} className="text-[#118AB2]" />
                          <span>Due: {new Date(list.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bottom - Quick Actions with elegant styling */}
                  <div className="border-t border-gray-100 dark:border-slate-700 p-3 bg-gradient-to-r from-gray-50/80 to-white dark:from-slate-700/50 dark:to-slate-800 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/shopping-list/${list.id}`)}
                      className="flex-1 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600
                                 hover:border-[#006D77] dark:hover:border-[#06D6A0] hover:bg-[#006D77]/5 dark:hover:bg-[#06D6A0]/10 hover:shadow-sm
                                 text-sm font-semibold text-gray-700 dark:text-gray-200 transition-all duration-200
                                 flex items-center justify-center gap-1.5"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    {!isArchived && !isCompleted && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/shopping-list/${list.id}`);
                        }}
                        className="flex-1 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600
                                   hover:border-[#06D6A0] hover:bg-[#06D6A0]/5 dark:hover:bg-[#06D6A0]/10 hover:shadow-sm
                                   text-sm font-semibold text-gray-700 dark:text-gray-200 transition-all duration-200
                                   flex items-center justify-center gap-1.5"
                      >
                        <PlusCircle size={14} className="text-[#06D6A0]" />
                        Add Item
                      </button>
                    )}

                    {!isArchived && !isCompleted && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteList(list.id);
                        }}
                        className="px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#06D6A0]/10 to-emerald-50 dark:from-[#06D6A0]/20 dark:to-emerald-900/30
                                   border-2 border-[#06D6A0]/30 dark:border-emerald-700 hover:border-[#06D6A0] hover:shadow-sm
                                   text-sm font-semibold text-[#059669] dark:text-emerald-300 transition-all duration-200
                                   flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle2 size={14} />
                        Complete
                      </button>
                    )}

                    <Tooltip label="Share this list with family or teammates so you can shop together.">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          promptShareList(list);
                        }}
                        className="px-3 py-2.5 rounded-xl bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600
                                   hover:border-[#118AB2] hover:bg-[#118AB2]/5 dark:hover:bg-[#118AB2]/10 hover:shadow-sm
                                   text-sm font-semibold text-gray-700 dark:text-gray-200 transition-all duration-200
                                   flex items-center justify-center gap-1.5"
                      >
                        <Share2 size={14} className="text-[#118AB2]" />
                        Share
                      </button>
                    </Tooltip>

                    <Tooltip label="Create a copy of this list with all its items.">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateList(list.id);
                          toast.success('List duplicated successfully');
                        }}
                        className="px-3 py-2.5 rounded-xl bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600
                                   hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:shadow-sm
                                   text-sm font-semibold text-gray-700 dark:text-gray-200 transition-all duration-200
                                   flex items-center justify-center gap-1.5"
                      >
                        <Copy size={14} className="text-amber-500" />
                        Duplicate
                      </button>
                    </Tooltip>

                    {isArchived ? (
                      <Tooltip label="Restore this list back to your active lists.">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnarchiveList(list.id);
                          }}
                          className="px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 hover:border-[#06D6A0] 
                                     hover:bg-[#06D6A0]/5 dark:hover:bg-[#06D6A0]/10 text-sm font-semibold text-[#06D6A0] transition-all
                                     flex items-center justify-center gap-1.5"
                        >
                          <ArchiveRestore size={14} />
                          Unarchive
                        </button>
                      </Tooltip>
                    ) : !isCompleted ? (
                      <Tooltip label="Archive this list to hide it without deleting any data.">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveList(list.id);
                          }}
                          className="px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 hover:border-gray-400 dark:hover:border-gray-500
                                     hover:bg-gray-100 dark:hover:bg-slate-600 text-sm font-semibold text-gray-700 dark:text-gray-200 transition-all
                                     flex items-center justify-center gap-1.5"
                        >
                          <Archive size={14} />
                          Archive
                        </button>
                      </Tooltip>
                    ) : null}

                    {/* Delete button for all lists */}
                    <Tooltip
                      label={hasBoughtItems
                        ? 'This list contains bought items and cannot be deleted (financial data protection).'
                        : 'Delete this list and all its items. This action cannot be undone.'}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasBoughtItems) {
                            toast.error('This list contains bought items and cannot be deleted.', {
                              duration: 5000,
                              icon: '🛡️'
                            });
                          } else {
                            promptDeleteList(list);
                          }
                        }}
                        className={`px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-sm font-semibold transition-all
                                   flex items-center justify-center gap-1.5
                                   ${hasBoughtItems 
                                     ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500' 
                                     : 'hover:border-red-500 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400'
                                   }`}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </Tooltip>
                  </div>
                </motion.div>
              );
            })}
            </div>
            <div ref={loadMoreTriggerRef} className="h-1 w-full" aria-hidden />
            {isFetchingMore && (
              <div className="flex items-center justify-center gap-3 py-6 text-sm font-semibold text-gray-600 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading more lists…
              </div>
            )}
            {nextCursor && (
              <div className="flex justify-center py-4">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingMore}
                  className="px-5 py-2 rounded-2xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:border-[#006D77]"
                >
                  {isFetchingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
            {!nextCursor && !isFetchingMore && lists.length > PAGE_SIZE && (
              <p className="py-4 text-center text-sm font-semibold text-gray-500 dark:text-gray-400">
                You’re all caught up.
              </p>
            )}
          </>
        )}
      </main>

      <Modal
        open={Boolean(deleteTarget)}
        title={deleteTarget ? `Delete "${deleteTarget.name}"?` : 'Delete list?'}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteList}
        confirmText="Delete"
        confirmLoading={deleteLoading}
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Deleting this list will remove all items permanently. This action cannot be undone.
        </p>
      </Modal>
      {shareTarget && (
        <ShareModal
          isOpen={Boolean(shareTarget)}
          onClose={closeShareModal}
          listId={shareTarget.id}
          listName={shareTarget.name}
          items={shareTarget.items || []}
        />
      )}
      <BottomNavigation />

      {/* Create List Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateListModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadLists();
            }}
            initialStep={createModalStartStep}
          />
        )}
      </AnimatePresence>

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveDropdown(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </div>
  );
}
